import React, { useMemo } from "react";
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import {
  LeadConversionProbability,
  LeadProbabilityPivotData,
} from "../../../../../models/Dashboard/LeadAnalytics/LeadAnalytics";

interface LeadProbabilityStagePivotTableProps {
  pipelineName: string;
  totalCount: number;
  pivotData: LeadProbabilityPivotData;
  selectedPipelineId: number | null;
  selectedStage: string | null;
  selectedProbability: LeadConversionProbability | null;
  onCellClick: (
    pipelineId: number,
    stage: string | null,
    probability: LeadConversionProbability | null
  ) => void;
  pipelineId: number;
}

const PROBABILITY_ROW_STYLES: Record<
  LeadConversionProbability,
  { bg: string; color: string; border: string }
> = {
  100: { bg: "#EDFAF3", color: "#157A4E", border: "#9ED9BC" },
  75: { bg: "#EEF3FF", color: "#3B52B4", border: "#B8C7F5" },
  50: { bg: "#FFF8E8", color: "#9A6B12", border: "#F0D9A2" },
  25: { bg: "#FFF1E8", color: "#B45309", border: "#F5C9A8" },
  0: { bg: "#F4F6FA", color: "#5C6785", border: "#D8DEE8" },
};

const STICKY_CELL_SX = {
  position: "sticky" as const,
  left: 0,
  zIndex: 2,
  boxShadow: "2px 0 6px rgba(30, 42, 68, 0.06)",
};

const STICKY_HEADER_SX = {
  ...STICKY_CELL_SX,
  zIndex: 4,
  backgroundColor: "#EEF1F8",
};

const formatPercentOfTotal = (count: number, total: number): string | null => {
  if (count <= 0 || total <= 0) return null;
  return `${Math.round((count / total) * 100)}%`;
};

const StageTotalCell: React.FC<{ count: number; total: number }> = ({ count, total }) => {
  const percent = formatPercentOfTotal(count, total);
  const hasValue = count > 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.6,
        minWidth: 44,
      }}
    >
      <Box
        component="span"
        sx={{
          fontWeight: 700,
          fontSize: "0.82rem",
          color: hasValue ? "#1E2A44" : "#B0B8C9",
          lineHeight: 1,
        }}
      >
        {count}
      </Box>
      {percent !== null && (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 36,
            px: 0.75,
            py: 0.25,
            borderRadius: "6px",
            backgroundColor: "#EEF1F8",
            border: "1px solid #DDE3F0",
            color: "#4A5568",
            fontSize: "0.64rem",
            fontWeight: 600,
            lineHeight: 1,
            letterSpacing: "0.02em",
          }}
        >
          {percent}
        </Box>
      )}
    </Box>
  );
};

const LeadProbabilityStagePivotTable: React.FC<LeadProbabilityStagePivotTableProps> = ({
  pipelineName,
  totalCount,
  pivotData,
  selectedPipelineId,
  selectedStage,
  selectedProbability,
  onCellClick,
  pipelineId,
}) => {
  const { stages, rows } = pivotData;

  const columnTotals = useMemo(() => {
    const totals: Record<string, number> = {};

    stages.forEach((stage) => {
      totals[stage] = 0;
    });

    rows.forEach((row) => {
      stages.forEach((stage) => {
        totals[stage] += row.counts[stage] ?? 0;
      });
    });

    return totals;
  }, [stages, rows]);

  const grandTotal = useMemo(
    () => Object.values(columnTotals).reduce((sum, value) => sum + value, 0),
    [columnTotals]
  );

  if (stages.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 120,
          border: "1px dashed #D8DEE8",
          borderRadius: 2,
          bgcolor: "#FBFCFF",
        }}
      >
        <Typography variant="body2" sx={{ color: "#5C6785" }}>
          No stage data available for {pipelineName}.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 1.25,
          flexWrap: "wrap",
        }}
      >
        <CustomTypography variant="heading7" sx={{ color: "#1E2A44", fontWeight: 700 }}>
          {pipelineName}
        </CustomTypography>
        <Chip
          label={`${totalCount} leads`}
          size="small"
          sx={{
            height: 24,
            fontSize: "0.72rem",
            fontWeight: 600,
            bgcolor: "#F0EDFF",
            color: "#6455ED",
            border: "1px solid #D9D2FF",
          }}
        />
      </Box>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          maxWidth: "100%",
          overflowX: "auto",
          borderRadius: 2,
          border: "1px solid #E2E8F4",
          bgcolor: "#FFFFFF",
          "&::-webkit-scrollbar": { height: 8 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#D8DEE8",
            borderRadius: 8,
          },
        }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: "auto",
            width: "max-content",
            minWidth: "100%",
          }}
        >
          <TableHead>
            <TableRow>
              <AppTableHeaderCell
                title="Probability"
                minWidth="96px"
                txtAlign="left"
                fontColor="#1E2A44"
                fontWeight={700}
                sx={{
                  ...STICKY_HEADER_SX,
                  fontSize: "0.74rem",
                  py: 1.1,
                  borderBottom: "1px solid #E2E8F4",
                }}
              />
              {stages.map((stage) => (
                <AppTableHeaderCell
                  key={stage}
                  title={stage}
                  minWidth="max-content"
                  txtAlign="center"
                  fontColor="#1E2A44"
                  fontWeight={600}
                  sx={{
                    fontSize: "0.7rem",
                    py: 1.1,
                    px: 1,
                    lineHeight: 1.3,
                    whiteSpace: "normal",
                    overflow: "visible",
                    wordBreak: "break-word",
                    borderBottom: "1px solid #E2E8F4",
                    backgroundColor: "#EEF1F8",
                    verticalAlign: "bottom",
                  }}
                />
              ))}
              <AppTableHeaderCell
                title="Total"
                minWidth="68px"
                txtAlign="center"
                fontColor="#1E2A44"
                fontWeight={700}
                sx={{
                  fontSize: "0.74rem",
                  py: 1.1,
                  borderBottom: "1px solid #E2E8F4",
                  backgroundColor: "#E8ECF6",
                  position: "sticky",
                  right: 0,
                  zIndex: 4,
                  boxShadow: "-2px 0 6px rgba(30, 42, 68, 0.06)",
                }}
              />
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row) => {
              const rowStyle = PROBABILITY_ROW_STYLES[row.probability];

              return (
                <TableRow
                  key={row.probability}
                  sx={{
                    "&:last-child td": { borderBottom: 0 },
                    "&:hover td": { filter: "brightness(0.99)" },
                  }}
                >
                  <AppTableRowCell
                    textAlign="left"
                    fontWeight={700}
                    fontSize="0.78rem"
                    color={rowStyle.color}
                    sx={{
                      ...STICKY_CELL_SX,
                      py: 1,
                      px: 1.25,
                      backgroundColor: rowStyle.bg,
                      borderLeft: `3px solid ${rowStyle.border}`,
                      borderBottom: "1px solid #EEF1F6",
                    }}
                  >
                    {row.probability}%
                  </AppTableRowCell>

                  {stages.map((stage) => {
                    const count = row.counts[stage] ?? 0;
                    const isSelected =
                      selectedPipelineId === pipelineId &&
                      selectedStage === stage &&
                      selectedProbability === row.probability;
                    const hasCount = count > 0;

                    return (
                      <AppTableRowCell
                        key={`${row.probability}-${stage}`}
                        fontSize="0.8rem"
                        fontWeight={hasCount ? 700 : 500}
                        color={hasCount ? "#1E2A44" : "#B0B8C9"}
                        onClick={
                          hasCount
                            ? () =>
                              onCellClick(
                                pipelineId,
                                isSelected ? null : stage,
                                isSelected ? null : row.probability
                              )
                            : undefined
                        }
                        sx={{
                          py: 0.9,
                          cursor: hasCount ? "pointer" : "default",
                          backgroundColor: isSelected ? "#EEEBFF" : "#FFFFFF",
                          borderBottom: "1px solid #EEF1F6",
                          transition: "background-color 0.15s ease, box-shadow 0.15s ease",
                          boxShadow: isSelected ? "0 0 0 2px #6455ED inset" : "none",
                          "&:hover": hasCount
                            ? {
                              backgroundColor: isSelected ? "#E4DEFF" : "#F7F6FF",
                            }
                            : undefined,
                        }}
                      >
                        {count}
                      </AppTableRowCell>
                    );
                  })}

                  <AppTableRowCell
                    fontSize="0.8rem"
                    fontWeight={700}
                    color="#1E2A44"
                    sx={{
                      py: 0.9,
                      backgroundColor: rowStyle.bg,
                      borderBottom: "1px solid #EEF1F6",
                      position: "sticky",
                      right: 0,
                      zIndex: 1,
                      boxShadow: "-2px 0 6px rgba(30, 42, 68, 0.06)",
                    }}
                  >
                    {row.rowTotal}
                  </AppTableRowCell>
                </TableRow>
              );
            })}

            <TableRow
              sx={{
                backgroundColor: "#F7F8FC",
                "& td": { borderTop: "2px solid #DDE3F0" },
              }}
            >
              <AppTableRowCell
                textAlign="left"
                fontSize="0.74rem"
                fontWeight={700}
                color="#1E2A44"
                sx={{
                  ...STICKY_CELL_SX,
                  py: 1.25,
                  px: 1.25,
                  backgroundColor: "#F7F8FC",
                  zIndex: 3,
                }}
              >
                Stage Total
              </AppTableRowCell>

              {stages.map((stage) => {
                const stageTotal = columnTotals[stage] ?? 0;
                return (
                  <AppTableRowCell
                    key={`total-${stage}`}
                    fontSize="0.78rem"
                    fontWeight={700}
                    color="#1E2A44"
                    sx={{ py: 1.25, px: 0.5, backgroundColor: "#F7F8FC" }}
                  >
                    <StageTotalCell count={stageTotal} total={grandTotal} />
                  </AppTableRowCell>
                );
              })}

              <AppTableRowCell
                fontSize="0.8rem"
                fontWeight={700}
                color="#6455ED"
                sx={{
                  py: 1.25,
                  px: 0.75,
                  backgroundColor: "#F0EDFF",
                  position: "sticky",
                  right: 0,
                  zIndex: 3,
                  boxShadow: "-2px 0 6px rgba(30, 42, 68, 0.06)",
                }}
              >
                {grandTotal}
              </AppTableRowCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default LeadProbabilityStagePivotTable;
